import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getErrorMessage, isApiError } from "@/app/http";
import type { FamilySummary, KidStatusSummary, PairingCodeSummary } from "../../contracts";
import {
  addAdminParent,
  createAdminFamily,
  createAdminPairingCode,
  deleteAdminFamily,
  listAdminFamilies,
  listAdminParents,
  listFamilyKids,
  removeAdminKid,
  removeAdminParent,
  unenrollAdminKid,
} from "../api";
import { screenBudgetQueryKeys } from "../queryKeys";

export interface FamilyParentSummary {
  userId: number;
  username: string;
  role: "owner" | "parent";
  createdAt: string;
  updatedAt: string;
}

interface UseScreenBudgetAdminDataOptions {
  onUnauthorized: () => void;
  currentUserId: number;
}

export function useScreenBudgetAdminData({ onUnauthorized, currentUserId }: UseScreenBudgetAdminDataOptions) {
  const queryClient = useQueryClient();
  const [selectedFamilyId, setSelectedFamilyId] = useState<number | null>(null);

  const [createFamilyName, setCreateFamilyName] = useState("");

  const [newParentUsername, setNewParentUsername] = useState("");
  const [pairingTtlSeconds, setPairingTtlSeconds] = useState("600");
  const [latestPairingCode, setLatestPairingCode] = useState<PairingCodeSummary | null>(null);

  const [deleteTargetFamilyId, setDeleteTargetFamilyId] = useState<number | null>(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState("");
  const [deleteConfirmCascade, setDeleteConfirmCascade] = useState(false);

  const familiesQuery = useQuery({
    queryKey: screenBudgetQueryKeys.adminFamilies,
    queryFn: async () => {
      const data = await listAdminFamilies();
      return data.families;
    },
  });

  const parentsQuery = useQuery({
    queryKey: screenBudgetQueryKeys.adminParents(selectedFamilyId),
    enabled: selectedFamilyId !== null,
    queryFn: async () => {
      if (selectedFamilyId === null) return [] as FamilyParentSummary[];
      const data = await listAdminParents(selectedFamilyId);
      return data.parents;
    },
  });

  const kidsQuery = useQuery({
    queryKey: screenBudgetQueryKeys.adminKids(selectedFamilyId),
    enabled: selectedFamilyId !== null,
    queryFn: async () => {
      if (selectedFamilyId === null) return [] as KidStatusSummary[];
      const data = await listFamilyKids(selectedFamilyId);
      return data.kids;
    },
  });

  const createFamilyMutation = useMutation({
    mutationFn: async (name: string) => createAdminFamily({ name }),
    onSuccess(data) {
      setCreateFamilyName("");
      setSelectedFamilyId(data.family.id);
      void queryClient.invalidateQueries({ queryKey: screenBudgetQueryKeys.adminFamilies });
    },
    onError(error) {
      if (isApiError(error) && error.status === 401) {
        onUnauthorized();
      }
    },
  });

  const deleteFamilyMutation = useMutation({
    mutationFn: async ({ familyId, confirmFamilyName, confirmCascade }: { familyId: number; confirmFamilyName: string; confirmCascade: boolean }) =>
      deleteAdminFamily(familyId, { confirmFamilyName, confirmCascade }),
    onSuccess(_data, variables) {
      if (selectedFamilyId === variables.familyId) {
        setSelectedFamilyId(null);
      }
      setLatestPairingCode(null);
      setDeleteTargetFamilyId(null);
      setDeleteConfirmName("");
      setDeleteConfirmCascade(false);
      void queryClient.invalidateQueries({ queryKey: screenBudgetQueryKeys.adminFamilies });
      void queryClient.invalidateQueries({ queryKey: screenBudgetQueryKeys.adminParents(variables.familyId) });
      void queryClient.invalidateQueries({ queryKey: screenBudgetQueryKeys.adminKids(variables.familyId) });
    },
    onError(error) {
      if (isApiError(error) && error.status === 401) {
        onUnauthorized();
      }
    },
  });

  const createPairingCodeMutation = useMutation({
    mutationFn: async (familyId: number) => {
      const parsedTtl = Number.parseInt(pairingTtlSeconds, 10);
      return createAdminPairingCode(familyId, { expiresInSeconds: Number.isFinite(parsedTtl) ? parsedTtl : 600 });
    },
    onSuccess(data) {
      setLatestPairingCode(data);
    },
    onError(error) {
      if (isApiError(error) && error.status === 401) {
        onUnauthorized();
      }
    },
  });

  const addParentMutation = useMutation({
    mutationFn: async ({ familyId, username }: { familyId: number; username: string }) => addAdminParent(familyId, { username }),
    onSuccess() {
      setNewParentUsername("");
      if (selectedFamilyId !== null) {
        void queryClient.invalidateQueries({ queryKey: screenBudgetQueryKeys.adminParents(selectedFamilyId) });
      }
    },
    onError(error) {
      if (isApiError(error) && error.status === 401) {
        onUnauthorized();
      }
    },
  });

  const removeParentMutation = useMutation({
    mutationFn: async ({ familyId, userId }: { familyId: number; userId: number }) => removeAdminParent(familyId, userId),
    onSuccess() {
      if (selectedFamilyId !== null) {
        void queryClient.invalidateQueries({ queryKey: screenBudgetQueryKeys.adminParents(selectedFamilyId) });
      }
    },
    onError(error) {
      if (isApiError(error) && error.status === 401) {
        onUnauthorized();
      }
    },
  });

  const unenrollKidMutation = useMutation({
    mutationFn: async (kidId: number) => unenrollAdminKid(kidId),
    onSuccess() {
      if (selectedFamilyId !== null) {
        void queryClient.invalidateQueries({ queryKey: screenBudgetQueryKeys.adminKids(selectedFamilyId) });
      }
    },
    onError(error) {
      if (isApiError(error) && error.status === 401) {
        onUnauthorized();
      }
    },
  });

  const removeKidMutation = useMutation({
    mutationFn: async (kidId: number) => removeAdminKid(kidId),
    onSuccess() {
      if (selectedFamilyId !== null) {
        void queryClient.invalidateQueries({ queryKey: screenBudgetQueryKeys.adminKids(selectedFamilyId) });
      }
    },
    onError(error) {
      if (isApiError(error) && error.status === 401) {
        onUnauthorized();
      }
    },
  });

  useEffect(() => {
    const families = familiesQuery.data ?? [];
    if (families.length === 0) {
      setSelectedFamilyId(null);
      return;
    }

    if (selectedFamilyId === null || !families.some(family => family.id === selectedFamilyId)) {
      setSelectedFamilyId(families[0]?.id ?? null);
      setLatestPairingCode(null);
    }
  }, [familiesQuery.data, selectedFamilyId]);

  useEffect(() => {
    const families = familiesQuery.data ?? [];
    if (deleteTargetFamilyId === null) return;
    if (!families.some(family => family.id === deleteTargetFamilyId)) {
      setDeleteTargetFamilyId(null);
      setDeleteConfirmName("");
      setDeleteConfirmCascade(false);
    }
  }, [deleteTargetFamilyId, familiesQuery.data]);

  useEffect(() => {
    if (
      (isApiError(familiesQuery.error) && familiesQuery.error.status === 401) ||
      (isApiError(parentsQuery.error) && parentsQuery.error.status === 401) ||
      (isApiError(kidsQuery.error) && kidsQuery.error.status === 401)
    ) {
      onUnauthorized();
    }
  }, [familiesQuery.error, kidsQuery.error, onUnauthorized, parentsQuery.error]);

  const errorMessage = useMemo(() => {
    const errors = [
      familiesQuery.error,
      parentsQuery.error,
      kidsQuery.error,
      createFamilyMutation.error,
      deleteFamilyMutation.error,
      addParentMutation.error,
      removeParentMutation.error,
      createPairingCodeMutation.error,
      unenrollKidMutation.error,
      removeKidMutation.error,
    ];

    for (const error of errors) {
      if (error && !(isApiError(error) && error.status === 401)) {
        return getErrorMessage(error, "Request failed");
      }
    }

    return null;
  }, [
    addParentMutation.error,
    createFamilyMutation.error,
    createPairingCodeMutation.error,
    deleteFamilyMutation.error,
    familiesQuery.error,
    kidsQuery.error,
    parentsQuery.error,
    removeKidMutation.error,
    removeParentMutation.error,
    unenrollKidMutation.error,
  ]);

  const families = familiesQuery.data ?? [];
  const parents = parentsQuery.data ?? [];
  const kids = kidsQuery.data ?? [];
  const selectedFamily = families.find(family => family.id === selectedFamilyId) ?? null;
  const deleteTargetFamily = families.find(family => family.id === deleteTargetFamilyId) ?? null;
  const isLoading = familiesQuery.isPending || (selectedFamilyId !== null && (parentsQuery.isPending || kidsQuery.isPending));

  const createFamily = async () => {
    const name = createFamilyName.trim();
    if (!name) return;
    await createFamilyMutation.mutateAsync(name);
  };

  const addParent = async () => {
    if (selectedFamilyId === null) return;
    const username = newParentUsername.trim();
    if (!username) return;
    await addParentMutation.mutateAsync({ familyId: selectedFamilyId, username });
  };

  const createPairingCode = async () => {
    if (selectedFamilyId === null) return;
    await createPairingCodeMutation.mutateAsync(selectedFamilyId);
  };

  const deleteFamily = async () => {
    if (!deleteTargetFamily) return;
    await deleteFamilyMutation.mutateAsync({
      familyId: deleteTargetFamily.id,
      confirmFamilyName: deleteConfirmName,
      confirmCascade: deleteConfirmCascade,
    });
  };

  const removeParent = async (userId: number) => {
    if (selectedFamilyId === null) return;
    await removeParentMutation.mutateAsync({ familyId: selectedFamilyId, userId });
  };

  const unenrollKid = async (kidId: number) => {
    await unenrollKidMutation.mutateAsync(kidId);
  };

  const removeKid = async (kidId: number) => {
    await removeKidMutation.mutateAsync(kidId);
  };

  const toggleDeleteFamily = (familyId: number) => {
    if (deleteTargetFamilyId === familyId) {
      setDeleteTargetFamilyId(null);
      setDeleteConfirmName("");
      setDeleteConfirmCascade(false);
      return;
    }

    setDeleteTargetFamilyId(familyId);
    setDeleteConfirmName("");
    setDeleteConfirmCascade(false);
  };

  return {
    currentUserId,
    selectedFamilyId,
    setSelectedFamilyId,
    createFamilyName,
    setCreateFamilyName,
    newParentUsername,
    setNewParentUsername,
    pairingTtlSeconds,
    setPairingTtlSeconds,
    latestPairingCode,
    deleteTargetFamilyId,
    setDeleteTargetFamilyId,
    deleteConfirmName,
    setDeleteConfirmName,
    deleteConfirmCascade,
    setDeleteConfirmCascade,
    families,
    parents,
    kids,
    selectedFamily,
    deleteTargetFamily,
    isLoading,
    errorMessage,
    createFamily,
    addParent,
    createPairingCode,
    deleteFamily,
    removeParent,
    unenrollKid,
    removeKid,
    toggleDeleteFamily,
    createFamilyMutation,
    deleteFamilyMutation,
    createPairingCodeMutation,
    addParentMutation,
    removeParentMutation,
    unenrollKidMutation,
    removeKidMutation,
  };
}
